import {getLocationMetaActivity, LocationContext} from "@ots-frontend/ots-data";
import {UNIT_LOAD_RING_SIZE, type UnitLoadRing, unitLoadRingDataUrl} from "@ots-frontend/ots-ui-tw";
import {Marker, MarkerIcon, MarkerProps} from "../Marker/Marker";
import TrailerIcon from "../../../../../assets/map/truck_icon.png";
import TrailerStationaryIcon from "../../../../../assets/map/truck_stationary_icon.png";
import {useMemo} from "react";

export type LocationContextMarkerProps =
  Omit<MarkerProps, "position" | "icon" | "label" | "opacity" | "onHover" | "onHoverOut" | "onMarkerClick">
  & {
  ctx: LocationContext;
  icon?: MarkerIcon | ((ctx: LocationContext) => MarkerIcon | undefined);
  iconColor?: string | ((ctx: LocationContext) => string | undefined)
  /** Faded: the unit has not pinged in a while. */
  stale?: boolean;
  /** Shown under the marker, e.g. the unit's name. */
  label?: string;
  /** How full the unit is, drawn as a ring around the marker; none when its contents aren't known. */
  loadRing?: UnitLoadRing;
  /** White reads on satellite imagery; pass a dark color over the road map. */
  labelColor?: string;
  onHover?: (e: google.maps.MapMouseEvent, ctx: LocationContext) => void;
  onHoverOut?: (e: google.maps.MapMouseEvent) => void;
  onMarkerClick?: (e: google.maps.MapMouseEvent, ctx: LocationContext) => void;
};

export const STALE_MARKER_OPACITY = 0.4;
export const MARKER_LABEL_CLASS = "ots-map-marker-label";

// The truck icons are 32x37; the label sits centered just below them.
const LABEL_ORIGIN = {x: 16, y: 46};
// Where the badge's center sits from the tip of the truck icon's pointer, which is where the marker is anchored: over
// the square's top right corner, as the load counts sit on the site pins.
const RING_BADGE_OFFSET = {x: 13, y: -34};

const ringIcons = new Map<string, google.maps.Icon>();
/** One icon per look, so a ping that leaves the fill alone doesn't reset the ring marker. */
const getRingIcon = (ring: UnitLoadRing): google.maps.Icon => {
  const key = `${ring.fill.toFixed(2)}|${ring.category}|${ring.estimated}`;
  const cached = ringIcons.get(key);
  if (cached) return cached;
  const half = UNIT_LOAD_RING_SIZE / 2;
  const icon = {
    url: unitLoadRingDataUrl(ring),
    scaledSize: new google.maps.Size(UNIT_LOAD_RING_SIZE, UNIT_LOAD_RING_SIZE),
    anchor: new google.maps.Point(half - RING_BADGE_OFFSET.x, half - RING_BADGE_OFFSET.y),
  };
  ringIcons.set(key, icon);
  return icon;
};

let labelStyleInjected = false;
/** A halo so the label reads over any imagery; Google only takes a class name for that. */
const ensureLabelStyle = () => {
  if (labelStyleInjected || typeof document === "undefined") return;
  labelStyleInjected = true;
  const style = document.createElement("style");
  style.textContent = `.${MARKER_LABEL_CLASS}{text-shadow:0 0 3px rgba(0,0,0,.85),0 0 1px rgba(0,0,0,.9);white-space:nowrap;}`;
  document.head.appendChild(style);
};

export const LocationContextMarker = (props: LocationContextMarkerProps) => {
  const {
    ctx,
    icon,
    stale = false,
    label,
    loadRing,
    labelColor = "#ffffff",
    options,
    onHover,
    onHoverOut,
    onMarkerClick,
    ...rest
  } = props;

  const position : google.maps.LatLngLiteral | undefined = useMemo(() => {
    if (ctx.location) {
      return {
        lat: ctx.location.latitude,
        lng: ctx.location.longitude
      }
    }
    return  undefined
  }, [ctx.location])

  const markerIcon = useMemo(() => {
    if (icon) {
      if (icon instanceof Function) {
        return icon(ctx);
      } else {
        return icon;
      }
    } else {
      const idle = getLocationMetaActivity(ctx.meta)?.status === "IDLE"
      return idle ? TrailerStationaryIcon : TrailerIcon
    }
  }, [icon, ctx])

  // MarkerF reads icon, label and opacity only when it creates the marker; options are applied on every change,
  // so they travel there. A null label clears it.
  const markerOptions = useMemo<google.maps.MarkerOptions>(() => {
    if (label) ensureLabelStyle();
    const labelled = label && typeof markerIcon === "string"
      ? {url: markerIcon, labelOrigin: new google.maps.Point(LABEL_ORIGIN.x, LABEL_ORIGIN.y)}
      : markerIcon;
    return {
      ...options,
      icon: labelled,
      opacity: stale ? STALE_MARKER_OPACITY : 1,
      label: label
        ? {text: label, color: labelColor, fontSize: "11px", fontWeight: "600", className: MARKER_LABEL_CLASS}
        : null,
    };
  }, [options, markerIcon, stale, label, labelColor])

  const ringIcon = loadRing && getRingIcon(loadRing);
  // Over the unit's corner but never clickable, so hovers and clicks still land on the unit.
  const ringOptions = useMemo<google.maps.MarkerOptions | undefined>(() => ringIcon && {
    icon: ringIcon,
    clickable: false,
    zIndex: (options?.zIndex ?? 0) + 1,
    opacity: stale ? STALE_MARKER_OPACITY : 1,
  }, [ringIcon, options?.zIndex, stale])

  if (!position) {
    return null
  }

  return (<>
    {ringOptions && <Marker position={position} options={ringOptions}/>}
    <Marker
      {...rest}
      position={position}
      options={markerOptions}
      onMouseOver={onHover ? (e) => onHover(e, ctx) : undefined}
      onMouseOut={onHoverOut ? (e) => onHoverOut(e) : undefined}
      onClick={onMarkerClick ? (e) => onMarkerClick(e, ctx) : undefined}
    />
  </>);
};
